import { Injectable, Injector, signal, inject } from '@angular/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MarketDataApiService } from '@api/api/marketData.service';
import { normalizeSymbolForApi } from '@shared/utils/symbol.utils';

/**
 * Shape the backend actually emits for `GET /api/v1/tickers/{symbol}` after the
 * `apiResponseInterceptor` unwraps the `{ success, data }` envelope.
 *
 * NOTE: the generated `TickerResponseSchema` is DRIFTED from this handler — the
 * Rust handler emits `lastPrice` / `changePercent24h` / `volume24h` (see
 * `crates/razor_app/src/api_server/routes/market_data.rs::get_ticker`), whereas
 * the OpenAPI-generated type claims `price` / `priceChangePercent24h`. We read
 * the real field names here rather than trust the drifted schema; fixing the
 * schema requires a backend annotation change + `./generate-apis.sh`.
 */
interface TickerPayload {
  readonly symbol?: string;
  readonly exchange?: string | null;
  /** Headline price as a decimal string, e.g. "97500.00". */
  readonly lastPrice?: string | null;
  /** 24h change as a percent string, e.g. "1.30" (not a fraction). */
  readonly changePercent24h?: string | null;
  /** 24h base-currency volume as a decimal string. */
  readonly volume24h?: string | null;
}

/** A single OHLCV candle as emitted by the gateway candles producer. */
interface CandlePayload {
  /** Closing price as a decimal string / number. */
  readonly close?: string | number | null;
}

/**
 * Shape of `GET /api/v1/public/candles/{symbol}`. The endpoint proxies the API
 * gateway verbatim, which wraps the series under a `{ status, data: { candles } }`
 * envelope (gateway `ApiResponse` uses `status`, NOT `success`, so the standard
 * `apiResponseInterceptor` leaves it intact). The candles array lives at
 * `data.candles`; each candle exposes `close`
 * (see `crates/razor_market_data/src/types/core.rs::Candle`).
 */
interface CandlesEnvelope {
  readonly data?: { readonly candles?: readonly CandlePayload[] | null } | null;
  readonly candles?: readonly CandlePayload[] | null;
}

/**
 * Centralised state for the shared symbol-hover card (GAP-FU-10).
 *
 * A single overlay instance lives in `app.component.html`. The
 * `SymbolHoverDirective` (GAP-010) calls `show(symbol, anchorRect)` on
 * hover/focus and `hide()` on blur/leave. Keeping one shared instance
 * avoids DOM bloat from per-element overlays.
 *
 * GAP-029: `show()` now drives a real backend quote feed. On every
 * (newly-hovered) symbol the service fetches the ticker
 * (`GET /api/v1/tickers/{symbol}`) for price / 1d Δ / volume / venue and
 * a short 1h candle series (`GET /api/v1/public/candles/{symbol}`) for the
 * sparkline trail, then populates `setQuote()`. Wiring the feed here keeps
 * both consumers — the inline hover card AND the symbol-search mini-quote
 * pane — fed from a single source without per-call-site duplication.
 *
 * On failure — including a ticker payload missing its headline price / 1d Δ —
 * we surface a real error state (`error()`); we never fall back to empty /
 * placeholder data, and we never coerce a missing headline value to `0`, so a
 * stuck "Loading quote…" can no longer masquerade as a successful (empty) quote.
 */
export interface SymbolQuote {
  readonly symbol: string;
  readonly price: number;
  /** 1-day change expressed as a fraction (0.025 === +2.5 %). */
  readonly delta1d: number;
  readonly volume?: number;
  readonly exchanges?: readonly string[];
  /** Recent close prices used for the sparkline trail. */
  readonly sparkline?: readonly number[];
}

/** Number of 1h candles pulled for the hover-card sparkline trail. */
const SPARKLINE_CANDLES = 24;

@Injectable({ providedIn: 'root' })
export class SymbolHoverService {
  private readonly injector = inject(Injector);

  private readonly _symbol = signal<string | null>(null);
  private readonly _anchor = signal<DOMRect | null>(null);
  private readonly _quote = signal<SymbolQuote | null>(null);
  private readonly _error = signal<string | null>(null);

  readonly symbol = this._symbol.asReadonly();
  readonly anchor = this._anchor.asReadonly();
  readonly quote = this._quote.asReadonly();
  /** Non-null when the latest quote fetch failed (so the UI can stop "Loading…"). */
  readonly error = this._error.asReadonly();

  /** Resolved lazily so unit tests can inject this service without HttpClient. */
  private marketDataApi?: MarketDataApiService;
  private fetchSub?: Subscription;
  /** Guards against an out-of-order response overwriting a newer hover. */
  private inFlightSymbol: string | null = null;

  show(symbol: string, anchor: DOMRect): void {
    this._symbol.set(symbol);
    this._anchor.set(anchor);

    // Same symbol already resolved — keep the cached quote, nothing to fetch.
    if (this._quote()?.symbol === symbol) {
      this._error.set(null);
      return;
    }

    // New symbol: clear stale data and pull a fresh quote from the backend.
    this._quote.set(null);
    this._error.set(null);
    this.fetchQuote(symbol);
  }

  hide(): void {
    this._symbol.set(null);
    this._anchor.set(null);
    // Cancel any in-flight fetch: without this a late response for a card the
    // user already dismissed still ran setQuote(), caching a quote that a
    // later re-hover would serve stale (the show() same-symbol short-circuit).
    this.fetchSub?.unsubscribe();
    this.inFlightSymbol = null;
  }

  setQuote(quote: SymbolQuote): void {
    this._quote.set(quote);
    this._error.set(null);
  }

  private fetchQuote(symbol: string): void {
    const api = this.resolveApi();
    if (!api) return;

    this.fetchSub?.unsubscribe();
    this.inFlightSymbol = symbol;

    const apiSymbol = normalizeSymbolForApi(symbol);

    const ticker$ = api
      .getTicker(apiSymbol)
      .pipe(map(t => t as unknown as TickerPayload));
    // Sparkline is best-effort: a candles gap must not blank the whole quote.
    const candles$ = api
      .getCandles(apiSymbol, '1h', undefined, undefined, SPARKLINE_CANDLES)
      .pipe(
        map(c => c as unknown as CandlesEnvelope),
        catchError(() => of<CandlesEnvelope>({})),
      );

    this.fetchSub = forkJoin({ ticker: ticker$, candles: candles$ }).subscribe({
      next: ({ ticker, candles }) => {
        // Drop the result if a newer hover superseded this request.
        if (this.inFlightSymbol !== symbol) return;
        const quote = this.toQuote(symbol, ticker, candles);
        if (!quote) {
          // Headline price/delta missing: surface an error rather than render a
          // fabricated $0.00 / 0.00% quote as if the fetch succeeded.
          this._quote.set(null);
          this._error.set(`No quote available for ${symbol}`);
          return;
        }
        this.setQuote(quote);
      },
      error: (err: unknown) => {
        if (this.inFlightSymbol !== symbol) return;
        this._quote.set(null);
        this._error.set(
          err instanceof Error ? err.message : `Failed to load quote for ${symbol}`,
        );
      },
    });
  }

  /**
   * Build a quote from the real backend payloads. Returns `null` when the
   * headline price or 1d change is missing/non-finite — the caller surfaces an
   * error state instead of coercing those to `0` (which would render a
   * fabricated quote that looks successful).
   */
  private toQuote(
    symbol: string,
    ticker: TickerPayload,
    candles: CandlesEnvelope,
  ): SymbolQuote | null {
    const price = Number(ticker.lastPrice);
    // changePercent24h is a percent string ("2.5"); the UI expects a fraction.
    const deltaPercent = Number(ticker.changePercent24h);
    if (!Number.isFinite(price) || !Number.isFinite(deltaPercent)) {
      return null;
    }

    const volume = Number(ticker.volume24h);
    const candleList = candles.data?.candles ?? candles.candles ?? [];
    const sparkline = candleList
      .map(c => Number(c.close))
      .filter(n => Number.isFinite(n));

    return {
      symbol,
      price,
      delta1d: deltaPercent / 100,
      volume: Number.isFinite(volume) ? volume : undefined,
      exchanges: ticker.exchange ? [ticker.exchange] : undefined,
      sparkline: sparkline.length >= 2 ? sparkline : undefined,
    };
  }

  private resolveApi(): MarketDataApiService | undefined {
    if (this.marketDataApi) return this.marketDataApi;
    try {
      // Lazy resolution: the generated API service depends on HttpClient, which
      // isn't provided in plain TestBed setups. In that case we simply skip the
      // network fetch (the card stays in its loading state) rather than throwing.
      this.marketDataApi = this.injector.get(MarketDataApiService);
    } catch {
      this.marketDataApi = undefined;
    }
    return this.marketDataApi;
  }
}
